import { useMemo } from "react";

import {
  Alert,
  Badge,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library";
import { faCreditCard, faFingerprint } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { EntityPayment } from "../../api/payments.generated";
import { truncateWithEllipsis } from "../../utils/stringUtils";

type Props = {
  isFetching: boolean;
  payments: Array<EntityPayment | undefined>;
};

type TableData = {
  userName: string;
  userID: string;
  status: string;
  paymentVendor: string;
  priceCurrencyCode: string;
  priceMicros: number;
  vendorProductID: string;
  createdAt: string;
};

export function PaymentsTable<T extends object>({
  isFetching,
  payments,
}: Props) {
  const { t } = useTranslation();

  const memoizedData: Array<TableData> = useMemo(
    () =>
      payments?.map((payment) => {
        return {
          userName: payment?.user?.name || "",
          userID: payment?.user?.id || "",
          status: payment?.status || "",
          paymentVendor: payment?.paymentVendor || "",
          priceCurrencyCode: payment?.priceCurrencyCode || "",
          priceMicros: payment?.priceMicros || 0,
          vendorProductID: payment?.vendorProductID || "",
          createdAt: payment?.createdAt || "",
          id: payment?.id || "",
        };
      }),
    [payments]
  );
  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          return (
            <Typography variant="body1" className="w-20">
              {truncateWithEllipsis(row?.original.userName, 30)}
            </Typography>
          );
        },
        id: "userName",
        accessor: "userName",
        width: "150px",
      },
      {
        Header: t("table.header.userID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original.id.toUpperCase().substr(0, 8)}
          </Badge>
        ),
        id: "userID",
        accessor: "userID",
        width: "150px",
      },
      {
        Header: t("table.header.status"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const status =
            row?.original.status === "ps-purchased"
              ? "success"
              : row?.original.status === "ps-canceled"
              ? "danger"
              : "warning";

          return (
            <Badge status={status} variant="pill">
              {row?.original.status.split("-")[1]}
            </Badge>
          );
        },
        id: "status",
        accessor: "status",
        width: "150px",
      },
      {
        Header: t("table.header.paymentVendor"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge variant="pill">
            {row?.original.paymentVendor?.split("-")[1] || ""}
          </Badge>
        ),
        id: "paymentVendor",
        accessor: "paymentVendor",
        width: "120px",
      },
      {
        Header: t("table.header.vendorProductID"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="min-w-0 break-all [&_*]:!whitespace-normal">
            <Badge
              variant="pill"
              leftIcon={
                <FontIcon
                  marginTop={4}
                  customIcon={
                    <FontAwesomeIcon size="lg" icon={faFingerprint} />
                  }
                />
              }
            >
              {row?.original.vendorProductID}
            </Badge>
          </div>
        ),
        id: "vendorProductID",
        accessor: "vendorProductID",
      },
    ],
    []
  );

  return (
    <div>
      {!isFetching && payments?.length === 0 ? (
        <Alert variant="error">{t("table.noData")}</Alert>
      ) : (
        <TableWithDefaultFilters
          tableData={memoizedData}
          tableColumns={columns}
          headerText={t("app.page.payments.heading")}
          headerIcon={
            <FontIcon
              marginTop={4}
              customIcon={<FontAwesomeIcon icon={faCreditCard} />}
            />
          }
        />
      )}
    </div>
  );
}
